"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { cn } from "@/lib/utils";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Layers,
  Sparkles,
  AlertCircle,
  Clock,
  CheckCircle2,
} from "lucide-react";

interface InvoiceItem {
  id: string;
  amount: string;
  dueDate: any;
  status: string;
  createdAt: any;
}

interface PaymentItem {
  id: string;
  amount: string;
  paymentDate: any;
  status: string;
}

interface CostItem {
  id: string;
  amount: string;
  category: string;
  description: string;
  createdAt: any;
}

interface OverviewClientProps {
  invoices: InvoiceItem[];
  payments: PaymentItem[];
  costs: CostItem[];
}

export function OverviewClient({
  invoices,
  payments,
  costs,
}: OverviewClientProps) {
  const router = useRouter();
  const [chartView, setChartView] = useState<
    "all" | "inflow" | "outflow" | "pending"
  >("all");
  const [expandedMonths, setExpandedMonths] = useState<Record<number, boolean>>(
    {},
  );

  // 1. Calculate metrics
  const totalInflow = payments
    .filter((p) => p.status === "cleared")
    .reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0);

  const totalOutflow = costs.reduce(
    (sum, c) => sum + parseFloat(c.amount.toString()),
    0,
  );
  const netCashFlow = totalInflow - totalOutflow;

  const pendingReceivables = invoices
    .filter((inv) => inv.status === "sent" || inv.status === "overdue")
    .reduce((sum, inv) => sum + parseFloat(inv.amount.toString()), 0);

  const overdueReceivables = invoices
    .filter((inv) => inv.status === "overdue")
    .reduce((sum, inv) => sum + parseFloat(inv.amount.toString()), 0);

  // 2. 90-day Forecast Projection
  const baseCashReserve = 45000;
  const startingCash = baseCashReserve + netCashFlow;

  const now = new Date();
  const getDaysDiff = (d: any) => {
    const targetDate = new Date(d);
    const diffTime = targetDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const toggleExpandMonth = (monthNum: number) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [monthNum]: !prev[monthNum],
    }));
  };

  const getMonthItems = (monthNum: number) => {
    const list: Array<{
      id: string;
      type: "inflow" | "outflow";
      title: string;
      date: Date;
      amount: number;
      url: string;
    }> = [];

    if (monthNum === 0) {
      // Historical payments as inflows
      payments.forEach((pay) => {
        if (pay.status === "cleared") {
          list.push({
            id: pay.id,
            type: "inflow",
            title: `Incasso da Pagamento (ID: ${pay.id})`,
            date: new Date(pay.paymentDate),
            amount: parseFloat(pay.amount),
            url: `/finance/invoices`,
          });
        }
      });
      // Historical costs as outflows
      costs.forEach((cost) => {
        list.push({
          id: cost.id,
          type: "outflow",
          title: `${cost.category} - ${cost.description || "Spesa registrata"}`,
          date: new Date(cost.createdAt),
          amount: parseFloat(cost.amount),
          url: `/explore?nodeId=${cost.id}`,
        });
      });
      return list.sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    invoices.forEach((inv) => {
      const days = getDaysDiff(inv.dueDate);
      const inBucket =
        monthNum === 1
          ? (inv.status === "sent" || inv.status === "overdue") && days <= 30
          : monthNum === 2
            ? inv.status === "sent" && days > 30 && days <= 60
            : inv.status === "sent" && days > 60 && days <= 90;

      if (inBucket) {
        list.push({
          id: inv.id,
          type: "inflow",
          title: `Fattura emessa (ID: ${inv.id})`,
          date: new Date(inv.dueDate),
          amount: parseFloat(inv.amount),
          url: `/finance/invoices/${inv.id}`,
        });
      }
    });

    costs.forEach((cost) => {
      const days = Math.abs(getDaysDiff(cost.createdAt));
      const inBucket =
        monthNum === 1
          ? days <= 30
          : monthNum === 2
            ? days > 30 && days <= 60
            : days > 60 && days <= 90;

      if (inBucket) {
        list.push({
          id: cost.id,
          type: "outflow",
          title: `Costo: ${cost.category} - ${cost.description || "Nessuna descrizione"}`,
          date: new Date(cost.createdAt),
          amount: parseFloat(cost.amount),
          url: `/explore?nodeId=${cost.id}`,
        });
      }
    });

    return list.sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const renderMonthItems = (monthNum: number) => {
    const items = getMonthItems(monthNum);

    if (items.length === 0) {
      return (
        <div className="text-center py-4 text-xs text-text-secondary font-medium">
          Nessun movimento previsto registrato per questo periodo.
        </div>
      );
    }

    return (
      <div className="px-2 py-1 select-none">
        <div className="text-[10px] uppercase font-bold text-text-muted tracking-wider mb-2">
          Dettaglio Movimenti ({items.length})
        </div>
        <div className="overflow-hidden border border-border/60 rounded-lg bg-card">
          <table className="w-full text-left border-collapse text-[11px]">
            <thead>
              <tr className="bg-card-muted/50 border-b border-border/60 font-semibold text-text-secondary">
                <th className="p-2.5">Data Scadenza/Registrazione</th>
                <th className="p-2.5">Tipo</th>
                <th className="p-2.5">Elemento / Descrizione</th>
                <th className="p-2.5 text-right">Importo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(item.url);
                  }}
                  className="hover:bg-primary-500/[0.03] cursor-pointer transition-colors group"
                >
                  <td className="p-2.5 text-text-secondary">
                    {item.date.toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </td>
                  <td className="p-2.5">
                    <span
                      className={cn(
                        "px-1.5 py-0.5 rounded-full text-[9px] font-bold border",
                        item.type === "inflow"
                          ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/15"
                          : "bg-rose-500/10 text-rose-600 border-rose-500/15",
                      )}
                    >
                      {item.type === "inflow" ? "Entrata" : "Uscita"}
                    </span>
                  </td>
                  <td className="p-2.5 font-medium text-text-primary group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </td>
                  <td
                    className={cn(
                      "p-2.5 text-right font-bold",
                      item.type === "inflow"
                        ? "text-emerald-600"
                        : "text-rose-600",
                    )}
                  >
                    {item.type === "inflow" ? "+" : "-"}
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Bucket 1: Days 1-30
  const inflow1 = invoices
    .filter((inv) => {
      const days = getDaysDiff(inv.dueDate);
      return (inv.status === "sent" || inv.status === "overdue") && days <= 30;
    })
    .reduce((sum, inv) => sum + parseFloat(inv.amount.toString()), 0);

  // Bucket 2: Days 31-60
  const inflow2 = invoices
    .filter((inv) => {
      const days = getDaysDiff(inv.dueDate);
      return inv.status === "sent" && days > 30 && days <= 60;
    })
    .reduce((sum, inv) => sum + parseFloat(inv.amount.toString()), 0);

  // Bucket 3: Days 61-90
  const inflow3 = invoices
    .filter((inv) => {
      const days = getDaysDiff(inv.dueDate);
      return inv.status === "sent" && days > 60 && days <= 90;
    })
    .reduce((sum, inv) => sum + parseFloat(inv.amount.toString()), 0);

  const monthlyCostEstimate = totalOutflow > 0 ? totalOutflow / 2 : 6000;

  // Month 1 Projection
  const startingCashM1 = startingCash;
  const expectedInflowM1 = inflow1 * 0.9 + overdueReceivables * 0.5;
  const expectedOutflowM1 = monthlyCostEstimate;
  const endingCashM1 = startingCashM1 + expectedInflowM1 - expectedOutflowM1;

  // Month 2 Projection
  const startingCashM2 = endingCashM1;
  const expectedInflowM2 = inflow2 * 0.9;
  const expectedOutflowM2 = monthlyCostEstimate;
  const endingCashM2 = startingCashM2 + expectedInflowM2 - expectedOutflowM2;

  // Month 3 Projection
  const startingCashM3 = endingCashM2;
  const expectedInflowM3 = inflow3 * 0.9;
  const expectedOutflowM3 = monthlyCostEstimate;
  const endingCashM3 = startingCashM3 + expectedInflowM3 - expectedOutflowM3;

  // Generate dynamic chart data based on active view filter
  const getChartDataAndColors = () => {
    switch (chartView) {
      case "inflow":
        return {
          title: "Previsione Entrate Cumulative (90 Giorni)",
          subtitle: "Transazioni saldate + proiezioni incassi fatture emesse",
          strokeColor: "#10b981", // Emerald
          gradientId: "gradient-inflow",
          gradientStopColor: "#10b981",
          prefix: "+",
          values: [
            { name: "Oggi", val: 0 },
            { name: "Prossimi 30gg", val: expectedInflowM1 },
            { name: "Prossimi 60gg", val: expectedInflowM1 + expectedInflowM2 },
            {
              name: "Prossimi 90gg",
              val: expectedInflowM1 + expectedInflowM2 + expectedInflowM3,
            },
          ],
        };
      case "outflow":
        return {
          title: "Previsione Costi Cumulativi (90 Giorni)",
          subtitle: "Stima uscite e costi di progetto ricorrenti",
          strokeColor: "#f43f5e", // Rose
          gradientId: "gradient-outflow",
          gradientStopColor: "#f43f5e",
          prefix: "-",
          values: [
            { name: "Oggi", val: 0 },
            { name: "Prossimi 30gg", val: expectedOutflowM1 },
            {
              name: "Prossimi 60gg",
              val: expectedOutflowM1 + expectedOutflowM2,
            },
            {
              name: "Prossimi 90gg",
              val: expectedOutflowM1 + expectedOutflowM2 + expectedOutflowM3,
            },
          ],
        };
      case "pending":
        return {
          title: "Scadenzario Crediti Attesi (90 Giorni)",
          subtitle: "Previsione collections su fatture sent/overdue",
          strokeColor: "#f59e0b", // Amber
          gradientId: "gradient-pending",
          gradientStopColor: "#f59e0b",
          prefix: "+",
          values: [
            { name: "Oggi", val: overdueReceivables },
            { name: "Prossimi 30gg", val: overdueReceivables + inflow1 },
            {
              name: "Prossimi 60gg",
              val: overdueReceivables + inflow1 + inflow2,
            },
            {
              name: "Prossimi 90gg",
              val: overdueReceivables + inflow1 + inflow2 + inflow3,
            },
          ],
        };
      case "all":
      default:
        return {
          title: "Proiezione Liquidità Totale (90 Giorni)",
          subtitle: "Stima cassa netta (saldo transazioni + entrate - uscite)",
          strokeColor: "#0f766e", // Teal
          gradientId: "gradient-cash",
          gradientStopColor: "#0f766e",
          prefix: "",
          values: [
            { name: "Oggi", val: startingCash },
            { name: "Prossimi 30gg", val: endingCashM1 },
            { name: "Prossimi 60gg", val: endingCashM2 },
            { name: "Prossimi 90gg", val: endingCashM3 },
          ],
        };
    }
  };

  const chartInfo = getChartDataAndColors();

  // SVG Chart Calculations
  const chartHeight = 200;
  const chartWidth = 500;
  const padding = 30;

  const rawValues = chartInfo.values.map((v) => v.val);
  const minVal = Math.min(...rawValues, 0) * 0.9;
  const maxVal = Math.max(...rawValues, 1000) * 1.1;
  const valRange = maxVal - minVal;

  const getX = (index: number) =>
    padding +
    (index * (chartWidth - padding * 2)) / (chartInfo.values.length - 1);
  const getY = (val: number) =>
    chartHeight -
    padding -
    ((val - minVal) * (chartHeight - padding * 2)) / valRange;

  const linePoints = chartInfo.values
    .map((d, i) => `${getX(i)},${getY(d.val)}`)
    .join(" ");

  const areaPoints =
    `${getX(0)},${getY(minVal)} ` +
    chartInfo.values.map((d, i) => `${getX(i)},${getY(d.val)}`).join(" ") +
    ` ${getX(chartInfo.values.length - 1)},${getY(minVal)}`;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div className="space-y-8">
      {/* 4 KPI Cards Grid with click handlers to filter plot */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {/* Card 1: Cassa */}
        <Card
          onClick={() => setChartView("all")}
          className={cn(
            "bg-card border shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all select-none",
            chartView === "all"
              ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
              : "border-border",
          )}
        >
          <CardContent className="p-5 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <span className="text-xs text-text-secondary font-semibold uppercase tracking-wider">
                Cassa Attuale (Stima)
              </span>
              <div className="p-1.5 bg-primary-500/10 text-primary-600 rounded-lg">
                <DollarSign className="h-4.5 w-4.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-text-primary">
                {formatCurrency(startingCash)}
              </h3>
              <p className="text-[10px] text-text-secondary mt-1 flex items-center gap-1">
                Base riserva + saldo transazioni
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Entrate */}
        <Card
          onClick={() => setChartView("inflow")}
          className={cn(
            "bg-card border shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all select-none",
            chartView === "inflow"
              ? "ring-2 ring-emerald-600 border-emerald-500 bg-emerald-500/[0.01]"
              : "border-border",
          )}
        >
          <CardContent className="p-5 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <span className="text-xs text-text-secondary font-semibold uppercase tracking-wider">
                Flusso Entrate
              </span>
              <div className="p-1.5 bg-emerald-500/10 text-emerald-600 rounded-lg">
                <ArrowUpRight className="h-4.5 w-4.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-emerald-600">
                {formatCurrency(totalInflow)}
              </h3>
              <p className="text-[10px] text-emerald-600 mt-1 font-medium flex items-center gap-0.5">
                <TrendingUp className="h-3.5 w-3.5" /> Transazioni saldate
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Uscite */}
        <Card
          onClick={() => setChartView("outflow")}
          className={cn(
            "bg-card border shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all select-none",
            chartView === "outflow"
              ? "ring-2 ring-rose-600 border-rose-500 bg-rose-500/[0.01]"
              : "border-border",
          )}
        >
          <CardContent className="p-5 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <span className="text-xs text-text-secondary font-semibold uppercase tracking-wider">
                Flusso Uscite (Costi)
              </span>
              <div className="p-1.5 bg-rose-500/10 text-rose-600 rounded-lg">
                <ArrowDownRight className="h-4.5 w-4.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-rose-600">
                {formatCurrency(totalOutflow)}
              </h3>
              <p className="text-[10px] text-rose-600 mt-1 font-medium flex items-center gap-0.5">
                <TrendingDown className="h-3.5 w-3.5" /> Spese operative e
                progetti
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Crediti */}
        <Card
          onClick={() => setChartView("pending")}
          className={cn(
            "bg-card border shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.01] transition-all select-none",
            chartView === "pending"
              ? "ring-2 ring-amber-600 border-amber-500 bg-amber-500/[0.01]"
              : "border-border",
          )}
        >
          <CardContent className="p-5 flex flex-col justify-between h-32">
            <div className="flex justify-between items-start">
              <span className="text-xs text-text-secondary font-semibold uppercase tracking-wider">
                Crediti in Attesa
              </span>
              <div className="p-1.5 bg-amber-500/10 text-amber-600 rounded-lg">
                <Clock className="h-4.5 w-4.5" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-amber-600">
                {formatCurrency(pendingReceivables)}
              </h3>
              <p className="text-[10px] text-text-secondary mt-1 flex items-center gap-1">
                <span>Incluso scaduto:</span>
                <span className="text-rose-600 font-bold">
                  {formatCurrency(overdueReceivables)}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 90-Day Cash Forecast Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Card */}
        <Card className="lg:col-span-2 bg-card border border-border shadow-sm">
          <CardHeader className="pb-2 border-b border-border/50">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary-500" /> {chartInfo.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="w-full flex justify-center">
              <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="w-full max-w-2xl h-auto overflow-visible"
              >
                <defs>
                  <linearGradient
                    id={chartInfo.gradientId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={chartInfo.gradientStopColor}
                      stopOpacity="0.15"
                    />
                    <stop
                      offset="100%"
                      stopColor={chartInfo.gradientStopColor}
                      stopOpacity="0.0"
                    />
                  </linearGradient>
                </defs>

                {/* Grid Lines */}
                <line
                  x1={padding}
                  y1={getY(minVal)}
                  x2={chartWidth - padding}
                  y2={getY(minVal)}
                  stroke="var(--border)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <line
                  x1={padding}
                  y1={getY(startingCash)}
                  x2={chartWidth - padding}
                  y2={getY(startingCash)}
                  stroke="var(--border)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <line
                  x1={padding}
                  y1={getY(maxVal)}
                  x2={chartWidth - padding}
                  y2={getY(maxVal)}
                  stroke="var(--border)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />

                {/* Shaded Area */}
                <polygon
                  points={areaPoints}
                  fill={`url(#${chartInfo.gradientId})`}
                />

                {/* Forecast Line */}
                <polyline
                  fill="none"
                  stroke={chartInfo.strokeColor}
                  strokeWidth={2.5}
                  points={linePoints}
                />

                {/* Chart Points */}
                {chartInfo.values.map((d, i) => (
                  <g key={i} className="group/point">
                    <circle
                      cx={getX(i)}
                      cy={getY(d.val)}
                      r={4.5}
                      fill={chartInfo.strokeColor}
                      stroke="#fff"
                      strokeWidth={1.5}
                    />
                    <text
                      x={getX(i)}
                      y={getY(d.val) - 10}
                      textAnchor="middle"
                      className="text-[9px] font-bold fill-text-primary"
                    >
                      {chartInfo.prefix}
                      {Math.round(d.val / 1000)}k
                    </text>
                  </g>
                ))}

                {/* Axis labels */}
                {chartInfo.values.map((d, i) => (
                  <text
                    key={`lbl-${i}`}
                    x={getX(i)}
                    y={chartHeight - 10}
                    textAnchor="middle"
                    className="text-[9px] font-semibold fill-text-secondary"
                  >
                    {d.name}
                  </text>
                ))}
              </svg>
            </div>
            <div className="mt-4 p-3 bg-card-muted/40 border border-border/50 rounded-lg flex items-center justify-between text-xs text-text-secondary">
              <span>{chartInfo.subtitle}</span>
              <span className="flex items-center gap-1 font-medium text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" /> Modello di calcolo
                dinamico 90-giorni
              </span>
            </div>
          </CardContent>
        </Card>

        {/* AI Advisor Panel */}
        <Card className="bg-card border border-border shadow-sm flex flex-col justify-between">
          <CardHeader className="pb-2 border-b border-border/50">
            <CardTitle className="text-sm font-bold flex items-center gap-1.5 text-text-primary">
              <Sparkles className="h-4.5 w-4.5 text-yellow-500 animate-pulse" />{" "}
              Advisor Finanziario AI
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 flex-1 flex flex-col justify-between space-y-4">
            <div className="space-y-3">
              <p className="text-xs text-text-secondary leading-relaxed">
                L&apos;analisi automatica dei flussi previsionali evidenzia le
                seguenti raccomandazioni strategiche:
              </p>

              <div className="space-y-2.5">
                <div className="p-3 bg-amber-500/[0.04] border border-amber-500/10 rounded-lg flex gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-semibold text-text-primary">
                      Rischio Scaduti
                    </h4>
                    <p className="text-[10px] text-text-secondary mt-0.5">
                      Ci sono €{overdueReceivables.toLocaleString("it-IT")} di
                      fatture scadute. Recuperare il 50% di questi crediti entro
                      i prossimi 15 giorni previene colli di bottiglia nel mese
                      2.
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-emerald-500/[0.04] border border-emerald-500/10 rounded-lg flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-semibold text-text-primary">
                      Liquidità Robusta
                    </h4>
                    <p className="text-[10px] text-text-secondary mt-0.5">
                      Le riserve rimangono stabili sopra la soglia critica di
                      sicurezza (€25k) per tutta la durata del trimestre
                      corrente.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4 text-[10px] text-text-muted flex justify-between items-center">
              <span>Modello di fatturazione: Mensile</span>
              <span className="underline cursor-pointer hover:text-foreground">
                Dettagli di simulazione
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown details table */}
      <Card className="bg-card border border-border shadow-sm">
        <CardHeader className="pb-2 border-b border-border/50">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary-500" /> Dettaglio Flussi e
            Proiezioni Mensili
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-card-muted/50 border-b border-border font-medium text-text-secondary">
                  <th className="p-4">Periodo Previsionale</th>
                  <th className="p-4">Liquidità Iniziale</th>
                  <th className="p-4 text-emerald-600">
                    Entrate Previste (Corrette)
                  </th>
                  <th className="p-4 text-rose-600">Uscite Previsionali</th>
                  <th className="p-4">Flusso Netto</th>
                  <th className="p-4 text-right">Saldo di Cassa Stimato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {/* Oggi */}
                <tr
                  onClick={() => toggleExpandMonth(0)}
                  className={cn(
                    "hover:bg-card-muted/20 cursor-pointer transition-colors select-none",
                    expandedMonths[0] && "bg-card-muted/30",
                    chartView === "all" && "bg-primary-500/[0.03]",
                  )}
                >
                  <td className="p-4 font-semibold text-text-primary flex items-center gap-1.5">
                    <span
                      className="text-[9px] text-text-muted transition-transform duration-200"
                      style={{
                        display: "inline-block",
                        transform: expandedMonths[0]
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▶
                    </span>
                    Oggi (Saldo Corrente)
                  </td>
                  <td className="p-4">-</td>
                  <td className="p-4">-</td>
                  <td className="p-4">-</td>
                  <td className="p-4">-</td>
                  <td className="p-4 text-right font-bold text-text-primary">
                    {formatCurrency(startingCash)}
                  </td>
                </tr>
                {expandedMonths[0] && (
                  <tr>
                    <td
                      colSpan={6}
                      className="bg-card-muted/10 p-4 border-b border-border"
                    >
                      {renderMonthItems(0)}
                    </td>
                  </tr>
                )}

                {/* Mese 1 */}
                <tr
                  onClick={() => toggleExpandMonth(1)}
                  className={cn(
                    "hover:bg-card-muted/20 cursor-pointer transition-colors select-none",
                    expandedMonths[1] && "bg-card-muted/30",
                    chartView === "all" && "bg-primary-500/[0.03]",
                  )}
                >
                  <td className="p-4 font-medium text-text-primary flex items-center gap-1.5">
                    <span
                      className="text-[9px] text-text-muted transition-transform duration-200"
                      style={{
                        display: "inline-block",
                        transform: expandedMonths[1]
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▶
                    </span>
                    Mese 1 (Prossimi 30gg)
                  </td>
                  <td className="p-4">{formatCurrency(startingCashM1)}</td>
                  <td className="p-4 text-emerald-600 font-medium">
                    +{formatCurrency(expectedInflowM1)}
                  </td>
                  <td className="p-4 text-rose-600 font-medium">
                    -{formatCurrency(expectedOutflowM1)}
                  </td>
                  <td className="p-4 font-semibold text-emerald-600">
                    {expectedInflowM1 - expectedOutflowM1 >= 0 ? "+" : ""}
                    {formatCurrency(expectedInflowM1 - expectedOutflowM1)}
                  </td>
                  <td className="p-4 text-right font-bold text-text-primary">
                    {formatCurrency(endingCashM1)}
                  </td>
                </tr>
                {expandedMonths[1] && (
                  <tr>
                    <td
                      colSpan={6}
                      className="bg-card-muted/10 p-4 border-b border-border"
                    >
                      {renderMonthItems(1)}
                    </td>
                  </tr>
                )}

                {/* Mese 2 */}
                <tr
                  onClick={() => toggleExpandMonth(2)}
                  className={cn(
                    "hover:bg-card-muted/20 cursor-pointer transition-colors select-none",
                    expandedMonths[2] && "bg-card-muted/30",
                    chartView === "all" && "bg-primary-500/[0.03]",
                  )}
                >
                  <td className="p-4 font-medium text-text-primary flex items-center gap-1.5">
                    <span
                      className="text-[9px] text-text-muted transition-transform duration-200"
                      style={{
                        display: "inline-block",
                        transform: expandedMonths[2]
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▶
                    </span>
                    Mese 2 (31-60gg)
                  </td>
                  <td className="p-4">{formatCurrency(startingCashM2)}</td>
                  <td className="p-4 text-emerald-600 font-medium">
                    +{formatCurrency(expectedInflowM2)}
                  </td>
                  <td className="p-4 text-rose-600 font-medium">
                    -{formatCurrency(expectedOutflowM2)}
                  </td>
                  <td className="p-4 font-semibold text-emerald-600">
                    {expectedInflowM2 - expectedOutflowM2 >= 0 ? "+" : ""}
                    {formatCurrency(expectedInflowM2 - expectedOutflowM2)}
                  </td>
                  <td className="p-4 text-right font-bold text-text-primary">
                    {formatCurrency(endingCashM2)}
                  </td>
                </tr>
                {expandedMonths[2] && (
                  <tr>
                    <td
                      colSpan={6}
                      className="bg-card-muted/10 p-4 border-b border-border"
                    >
                      {renderMonthItems(2)}
                    </td>
                  </tr>
                )}

                {/* Mese 3 */}
                <tr
                  onClick={() => toggleExpandMonth(3)}
                  className={cn(
                    "hover:bg-card-muted/20 cursor-pointer transition-colors select-none",
                    expandedMonths[3] && "bg-card-muted/30",
                    chartView === "all" && "bg-primary-500/[0.03]",
                  )}
                >
                  <td className="p-4 font-medium text-text-primary flex items-center gap-1.5">
                    <span
                      className="text-[9px] text-text-muted transition-transform duration-200"
                      style={{
                        display: "inline-block",
                        transform: expandedMonths[3]
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▶
                    </span>
                    Mese 3 (61-90gg)
                  </td>
                  <td className="p-4">{formatCurrency(startingCashM3)}</td>
                  <td className="p-4 text-emerald-600 font-medium">
                    +{formatCurrency(expectedInflowM3)}
                  </td>
                  <td className="p-4 text-rose-600 font-medium">
                    -{formatCurrency(expectedOutflowM3)}
                  </td>
                  <td className="p-4 font-semibold text-emerald-600">
                    {expectedInflowM3 - expectedOutflowM3 >= 0 ? "+" : ""}
                    {formatCurrency(expectedInflowM3 - expectedOutflowM3)}
                  </td>
                  <td className="p-4 text-right font-bold text-text-primary">
                    {formatCurrency(endingCashM3)}
                  </td>
                </tr>
                {expandedMonths[3] && (
                  <tr>
                    <td
                      colSpan={6}
                      className="bg-card-muted/10 p-4 border-b border-border"
                    >
                      {renderMonthItems(3)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
